import HttpFactory from '../HttpFactory'
import type IBasicObjectResponse from '../interfaces/IBasicObjectResponse'
import type { GuestProfileUpdate, GuestUser } from '~/types/guest'

interface IGuestResponse extends IBasicObjectResponse {
  data: GuestUser
}

// Cognito endpoints return either the logged-in user (GuestUser) or a status
// object that tells the frontend which step is required next:
// - confirm_required: email verification code must be confirmed first.
// - already_confirmed: account is already confirmed, user should just log in.
// - google_only: guest has Google but no Cognito password (F0).
interface IGuestStatus {
  status: 'confirm_required' | 'already_confirmed' | 'google_only'
}

interface ICognitoLoginResponse extends IBasicObjectResponse {
  data: GuestUser | IGuestStatus
}

interface ICognitoStatusResponse extends IBasicObjectResponse {
  data: object | IGuestStatus
}

const extraHeaders = {
  'X-Requested-With': 'XMLHttpRequest'
}

class GuestModule extends HttpFactory {
  private RESOURCE = 'guest'

  async loginWithGoogleFromClient (credential: string): Promise<IGuestResponse> {
    return await this.callFetch<IGuestResponse>(
      'POST',
      `${this.RESOURCE}/auth/login-with-google`,
      { credential },
      extraHeaders
    )
  }

  // register=true performs an explicit sign-up; a plain login never creates
  // an account (prevents ghost accounts from email typos).
  async loginWithCognitoFromClient (email: string, password: string, register = false): Promise<ICognitoLoginResponse> {
    return await this.callFetch<ICognitoLoginResponse>(
      'POST',
      `${this.RESOURCE}/auth/cognito/login-with-cognito`,
      { email, password, register },
      extraHeaders
    )
  }

  async confirmSignUpFromClient (email: string, code: string): Promise<ICognitoLoginResponse> {
    return await this.callFetch<ICognitoLoginResponse>(
      'POST',
      `${this.RESOURCE}/auth/cognito/confirm-sign-up`,
      { email, code },
      extraHeaders
    )
  }

  async resendSignUpConfirmationCodeFromClient (email: string): Promise<ICognitoStatusResponse> {
    return await this.callFetch<ICognitoStatusResponse>(
      'POST',
      `${this.RESOURCE}/auth/cognito/resend-sign-up-confirmation-code`,
      { email },
      extraHeaders
    )
  }

  async sendPasswordResetCodeFromClient (email: string): Promise<ICognitoStatusResponse> {
    return await this.callFetch<ICognitoStatusResponse>(
      'POST',
      `${this.RESOURCE}/auth/cognito/send-password-reset-code`,
      { email },
      extraHeaders
    )
  }

  async confirmPasswordResetFromClient (email: string, code: string, newPassword: string): Promise<IBasicObjectResponse> {
    return await this.callFetch<IBasicObjectResponse>(
      'POST',
      `${this.RESOURCE}/auth/cognito/confirm-password-reset`,
      { email, code, new_password: newPassword },
      extraHeaders
    )
  }

  async getSessionFromClient (): Promise<IGuestResponse> {
    return await this.callFetch<IGuestResponse>(
      'POST',
      `${this.RESOURCE}/get-session`,
      null,
      extraHeaders
    )
  }

  async logoutFromClient ():  Promise<IBasicObjectResponse<object>> {
    return await this.callFetch<IBasicObjectResponse>(
      'POST',
      `${this.RESOURCE}/auth/logout`,
      null,
      extraHeaders
    )
  }

  async updateProfileFromClient (payload: GuestProfileUpdate): Promise<IGuestResponse> {
    return await this.callFetch<IGuestResponse>(
      'POST',
      `${this.RESOURCE}/profile/update-profile`,
      payload,
      extraHeaders
    )
  }

  async deleteAccountFromClient (payload: { confirmation: string }): Promise<IBasicObjectResponse<object>> {
    return await this.callFetch<IBasicObjectResponse>(
      'POST',
      `${this.RESOURCE}/profile/delete-account`,
      payload,
      extraHeaders
    )
  }

  // TR-40866: guest answers the newsletter opt-in prompt. subscribe triggers
  // the double-opt-in mail server-side; either answer marks the prompt as
  // handled so it is never shown again.
  async answerNewsletterPromptFromClient (
    decision: 'subscribe' | 'dismiss',
    uiVariant?: 'modal' | 'one_tap' | 'returning',
  ): Promise<IGuestResponse> {
    return await this.callFetch<IGuestResponse>(
      'POST',
      `${this.RESOURCE}/newsletter/answer-prompt`,
      { decision, ui_variant: uiVariant },
      extraHeaders
    )
  }

  async logEventFromClient (
    event: string,
    payload: Record<string, string | number | boolean> = {}
  ): Promise<IBasicObjectResponse> {
    return await this.callFetch<IBasicObjectResponse>(
      'POST',
      `${this.RESOURCE}/log-event`,
      { event, payload },
      extraHeaders
    )
  }
}

export default GuestModule
